import { useCallback, useEffect, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { MdEdit, MdAdd, MdSync } from 'react-icons/md';
import { APP_2_CHANNELS, getChannelPrettyName } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { useSetError } from '../../redux/modals';
import { BPMTable, TypedHeader } from '../../Components/StickyTable';
import { Button } from '../../Components/Button';
import { Dropdown } from '../../Components/Dropdown';
import { IconButton } from '../../Components/IconButton';
import { TableLoading } from '../../Components/TableEmptyState';
import { TablePage } from '../../Components/TablePage';
import { AlertDialog } from '../../Components/AlertDialog';
import { Appearance, Design, Intent, Placement, Size } from '../../Components/designConstants';
import { checkGoogleAccessTokens } from '../../utils/auth';
import {
  addBlueprint,
  Blueprint,
  editBlueprint,
  syncBlueprint,
  useBlueprints
} from './use-blueprints';

const CHANNEL_OPTIONS: { label: string; value: string }[] = APP_2_CHANNELS.map(channel => ({
  label: getChannelPrettyName(channel),
  value: channel
}));

export const Blueprints = () => {
  const setError = useSetError();
  const { data: blueprints, isLoading, error, refetch } = useBlueprints();
  const [editingBlueprint, setEditingBlueprint] = useState<Blueprint | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  // Portals BPMTable's filter bar into the WidgetContainer header, next to rightActions,
  // instead of its default spot above the table -- see KPIStatus for the same pattern.
  const [filterBarContainer, setFilterBarContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load blueprints",
        message: `Failed to load blueprints: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  const handleSyncBlueprint = async (blueprint: Blueprint) => {
    try {
      setSyncingId(blueprint.id);
      const googleAccessToken = await checkGoogleAccessTokens(setError);
      await syncBlueprint(blueprint.presentation_id, googleAccessToken);
      setShowSuccessDialog(true);
    } catch (e) {
      setError({ message: `Error syncing blueprint: ${e.message}`, reportError: e });
    } finally {
      setSyncingId(null);
    }
  };

  const renderTableLoading = useCallback(
    () => (
      <TableLoading
        isLoading={isLoading}
        error={error}
        emptyMessage='No blueprints to show'
        rowHeight={80}
        headerHeight={50}
      />
    ),
    [isLoading, error]
  );

  const tableHeaders: TypedHeader<Blueprint>[] = [
    {
      label: 'Sync',
      name: 'sync',
      width: 75,
      nonFilterable: true,
      nonInteractive: true,
      renderer: blueprint => (
        <IconButton
          onClick={() => handleSyncBlueprint(blueprint)}
          loading={syncingId === blueprint.id}
          disabled={syncingId !== null}
        >
          <MdSync />
        </IconButton>
      )
    },
    { label: 'Presentation ID', name: 'presentation_id', width: 400 },
    {
      label: 'Channel',
      name: 'channel',
      width: 200,
      // Filter/sort on the pretty name shown in the cell, not the raw channel key --
      // optionsGetter drives both the filter dropdown's value list and its match logic.
      contentGetter: blueprint => getChannelPrettyName(blueprint.channel),
      optionsGetter: blueprint => [getChannelPrettyName(blueprint.channel)],
      renderer: blueprint => getChannelPrettyName(blueprint.channel)
    },
    { label: 'Name', name: 'name', flex: 1 },
    { label: 'Description', name: 'description', flex: 1 },
    {
      label: 'Edit',
      name: 'edit',
      width: 75,
      nonFilterable: true,
      nonInteractive: true,
      renderer: blueprint => (
        <IconButton onClick={() => setEditingBlueprint(blueprint)}>
          <MdEdit />
        </IconButton>
      )
    }
  ];

  return (
    <>
      <TablePage
        header={<div className='widget-filter-bar-container' ref={setFilterBarContainer} />}
        rightActions={
          <Button size={Size.LG} onClick={() => setShowAddModal(true)} leadingElement={<MdAdd />}>
            Add Blueprint
          </Button>
        }
      >
        <BPMTable
          data={blueprints ?? []}
          externalFilterBarContainer={filterBarContainer}
          headerHeight={50}
          headers={tableHeaders}
          noRowsRenderer={renderTableLoading}
          rowHeight={80}
        />
      </TablePage>

      <EditBlueprintModal
        blueprint={editingBlueprint}
        onClose={() => setEditingBlueprint(null)}
        onSaved={() => {
          setEditingBlueprint(null);
          refetch();
        }}
      />

      <AddBlueprintModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSaved={() => {
          setShowAddModal(false);
          refetch();
        }}
      />

      <AlertDialog
        intent={Intent.SUCCESS}
        show={showSuccessDialog}
        onHide={() => setShowSuccessDialog(false)}
        title='Success!'
        body='The blueprint has been synced successfully.'
        primaryAction={{ label: 'Close' }}
      />
    </>
  );
};

interface EditBlueprintModalProps {
  blueprint: Blueprint | null;
  onClose: () => void;
  onSaved: () => void;
}

const EditBlueprintModal = ({ blueprint, onClose, onSaved }: EditBlueprintModalProps) => {
  const setError = useSetError();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (blueprint) {
      setName(blueprint.name);
      setDescription(blueprint.description);
    }
  }, [blueprint]);

  const handleSave = async () => {
    if (!blueprint) {
      return;
    }
    try {
      setSaving(true);
      await editBlueprint({ blueprintId: blueprint.id, name, description });
      onSaved();
    } catch (e) {
      setError({ message: `Error updating blueprint: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={Boolean(blueprint)} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Blueprint</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className='mb-3'>
          <Form.Label>Name</Form.Label>
          <Form.Control type='text' value={name} onChange={e => setName(e.target.value)} />
        </Form.Group>
        <Form.Group className='mb-3'>
          <Form.Label>Description</Form.Label>
          <Form.Control
            type='text'
            value={description}
            onChange={e => setDescription(e.target.value)}
            as='textarea'
            rows={3}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button loading={saving} onClick={handleSave}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface AddBlueprintModalProps {
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const AddBlueprintModal = ({ show, onClose, onSaved }: AddBlueprintModalProps) => {
  const setError = useSetError();
  const [presentationId, setPresentationId] = useState('');
  const [channel, setChannel] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!show) {
      setPresentationId('');
      setChannel('');
      setName('');
      setDescription('');
    }
  }, [show]);

  const handleAdd = async () => {
    if (!presentationId || !channel || !name) {
      setError({ message: 'Required fields: Presentation ID, Channel, and Name' });
      return;
    }
    try {
      setAdding(true);
      await addBlueprint({ presentationId, channel, name, description });
      onSaved();
    } catch (e) {
      setError({ message: `Error creating blueprint: ${e.message}`, reportError: e });
    } finally {
      setAdding(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Add New Blueprint</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>Presentation ID *</Form.Label>
          <Form.Control
            type='text'
            value={presentationId}
            onChange={e => setPresentationId(e.target.value)}
            placeholder='Enter presentation ID'
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Channel *</Form.Label>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            options={CHANNEL_OPTIONS}
            value={channel}
            onChange={setChannel}
            placeholder='Select a channel'
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Name *</Form.Label>
          <Form.Control
            type='text'
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder='Enter name of the blueprint'
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Description</Form.Label>
          <Form.Control
            type='text'
            value={description}
            onChange={e => setDescription(e.target.value)}
            as='textarea'
            rows={3}
            placeholder='Enter description'
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={onClose}
          disabled={adding}
        >
          Cancel
        </Button>
        <Button loading={adding} onClick={handleAdd}>
          Add Blueprint
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
